/**
 * Map legend component
 * Legend overlay for map visualizations
 */

import { Space, Typography } from 'antd';
import type { GridCellStatus } from '@/types/grid';
import { CELL_STATUS_CONFIG, CELL_STATUS_COLORS } from '@/utils/constants';

const { Text } = Typography;

interface MapLegendProps {
  type?: 'cells' | 'places' | 'custom';
  items?: Array<{ color: string; label: string }>;
  position?: 'bottomleft' | 'bottomright' | 'topleft' | 'topright';
  title?: string;
}

export function MapLegend({
  type = 'cells',
  items,
  position = 'bottomright',
  title,
}: MapLegendProps) {
  const positionStyles: Record<string, React.CSSProperties> = {
    bottomleft: { bottom: 30, left: 10 },
    bottomright: { bottom: 30, right: 10 },
    topleft: { top: 10, left: 10 },
    topright: { top: 10, right: 60 },
  };

  const getLegendItems = (): Array<{ color: string; label: string }> => {
    if (items) return items;

    if (type === 'cells') {
      const statuses: GridCellStatus[] = ['pending', 'processing', 'completed', 'failed', 'skipped'];
      return statuses.map((status) => ({
        color: CELL_STATUS_COLORS[status],
        label: CELL_STATUS_CONFIG[status].label,
      }));
    }

    if (type === 'places') {
      return [
        { color: '#722ed1', label: 'Place location' },
        { color: '#1677ff', label: 'Selected' },
      ];
    }

    return [];
  };

  const legendItems = getLegendItems();

  return (
    <div
      style={{
        position: 'absolute',
        ...positionStyles[position],
        zIndex: 1000,
        background: 'rgba(255, 255, 255, 0.95)',
        borderRadius: 8,
        padding: '8px 12px',
        boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
      }}
    >
      {title && (
        <Text strong style={{ display: 'block', marginBottom: 8, fontSize: 12 }}>
          {title}
        </Text>
      )}
      <Space direction="vertical" size={4}>
        {legendItems.map((item, index) => (
          <div
            key={index}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 8,
            }}
          >
            <div
              style={{
                width: 14,
                height: 14,
                backgroundColor: item.color,
                borderRadius: 3,
                border: '1px solid rgba(0,0,0,0.1)',
              }}
            />
            <Text style={{ fontSize: 12 }}>{item.label}</Text>
          </div>
        ))}
      </Space>
    </div>
  );
}

/**
 * Compact legend as a single row
 */
export function MapLegendCompact({
  items,
}: {
  items: Array<{ color: string; label: string }>;
}) {
  return (
    <Space size={12} wrap>
      {items.map((item, index) => (
        <Space key={index} size={4}>
          <div
            style={{
              width: 12,
              height: 12,
              backgroundColor: item.color,
              borderRadius: 2,
            }}
          />
          <Text style={{ fontSize: 12 }}>{item.label}</Text>
        </Space>
      ))}
    </Space>
  );
}

/**
 * Stats overlay for map
 */
export function MapStats({
  stats,
  position = 'topleft',
}: {
  stats: Array<{ label: string; value: string | number }>;
  position?: 'bottomleft' | 'bottomright' | 'topleft' | 'topright';
}) {
  const positionStyles: Record<string, React.CSSProperties> = {
    bottomleft: { bottom: 30, left: 10 },
    bottomright: { bottom: 30, right: 10 },
    topleft: { top: 10, left: 60 },
    topright: { top: 10, right: 60 },
  };

  return (
    <div
      style={{
        position: 'absolute',
        ...positionStyles[position],
        zIndex: 1000,
        background: 'rgba(255, 255, 255, 0.95)',
        borderRadius: 8,
        padding: '8px 12px',
        boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
      }}
    >
      <Space size={16}>
        {stats.map((stat, index) => (
          <div key={index}>
            <Text type="secondary" style={{ fontSize: 10, display: 'block' }}>
              {stat.label}
            </Text>
            <Text strong style={{ fontSize: 14 }}>
              {stat.value}
            </Text>
          </div>
        ))}
      </Space>
    </div>
  );
}

export default MapLegend;
